import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AuthService } from '../../services/auth.service';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';

interface RSAMigrationModalTriggerProps {
    children?: React.ReactNode;
}

/**
 * RSA Migration Modal Trigger Component
 * 
 * This component checks sessionStorage for migration flags after login
 * and shows the mandatory RSA migration modal after a 2-second delay.
 * It should be placed in the main vault/all-items page.
 */
const RSAMigrationModalTrigger: React.FC<RSAMigrationModalTriggerProps> = () => {
    const { logout } = useAuth();
    const [showMigrationModal, setShowMigrationModal] = useState(false);
    const [migrating, setMigrating] = useState(false);
    const [email, setEmail] = useState('');
    const [encryptionKey, setEncryptionKey] = useState('');
    const pendingTimeoutRef = useRef<number | undefined>(undefined);

    const checkMigrationFlags = useCallback(() => {
        const migrationPending = sessionStorage.getItem('rsa_migration_pending');
        const migrationEmail = sessionStorage.getItem('rsa_migration_email');
        const migrationKey = sessionStorage.getItem('rsa_migration_encryption_key');
        const showTime = sessionStorage.getItem('rsa_migration_show_time');

        if (migrationPending === 'true' && migrationEmail && migrationKey && showTime) {
            const loginTime = parseInt(showTime);
            const now = Date.now();
            const elapsed = now - loginTime;
            const delay = Math.max(2000 - elapsed, 0);

            console.log(`🔒 Migration pending. Showing modal in ${delay}ms`);

            if (pendingTimeoutRef.current) {
                clearTimeout(pendingTimeoutRef.current);
            }

            pendingTimeoutRef.current = window.setTimeout(() => {
                setEmail(migrationEmail);
                setEncryptionKey(migrationKey);
                setShowMigrationModal(true);
            }, delay);
        }
    }, []);

    useEffect(() => {
        checkMigrationFlags();
        const handler = () => checkMigrationFlags();
        window.addEventListener('rsa-migration-flag-updated', handler);

        return () => {
            window.removeEventListener('rsa-migration-flag-updated', handler);
            if (pendingTimeoutRef.current) {
                clearTimeout(pendingTimeoutRef.current);
            }
        };
    }, [checkMigrationFlags]);

    const handleMandatoryMigration = async () => {
        try {
            setMigrating(true);
            
            console.log("🔒 Starting MANDATORY RSA migration...");
            
            // Get both keys needed for migration
            const vaultKey = localStorage.getItem('vaultKey');
            if (!vaultKey) {
                throw new Error('Vault key not found. Please log in again.');
            }
            
            // Call the migration API with both keys
            const result = await AuthService.migrateToRSA(vaultKey, encryptionKey);
            
            if (result.success) {
                toast.success('✅ Security upgrade complete! Don\'t login now, We will inform you to log in again.', {
                    duration: 6000
                });
                
                // Clear sessionStorage flags
                sessionStorage.removeItem('rsa_migration_pending');
                sessionStorage.removeItem('rsa_migration_email');
                sessionStorage.removeItem('rsa_migration_encryption_key');
                sessionStorage.removeItem('rsa_migration_show_time');
                
                // Close modal
                setShowMigrationModal(false);
                setMigrating(false);
                
                // Logout user (they need to login again with new encryption)
                await logout();
                
                // Show success message
                toast.success('Please log in again to complete the security upgrade.');
            } else {
                throw new Error(result.message || 'Migration failed');
            }
        } catch (error: any) {
            console.error('Migration error:', error);
            setMigrating(false);
            toast.error(error.message || 'Security upgrade failed. Please try again.');
        }
    };

    if (!showMigrationModal) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-[10000] overflow-y-auto bg-black bg-opacity-75 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-8">
                <div className="text-center mb-6">
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 mb-4">
                        <svg className="h-10 w-10 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        🔒 Security Upgrade Required
                    </h3>
                    <p className="text-sm text-gray-600">
                        Your account uses an older encryption method
                    </p>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h4 className="text-sm font-medium text-blue-800">What's happening?</h4>
                            <p className="text-sm text-blue-700 mt-1">
                                We're upgrading all accounts to <strong>RSA-2048 encryption</strong> with enhanced security features. 
                                This is a one-time, mandatory upgrade that will take just a moment.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="space-y-3 mb-6">
                    <div className="flex items-start">
                        <svg className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                            <p className="font-medium text-gray-900">Stronger Encryption</p>
                            <p className="text-sm text-gray-600">RSA-2048 with AES-256-GCM hybrid encryption</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <svg className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                            <p className="font-medium text-gray-900">Enhanced Security</p>
                            <p className="text-sm text-gray-600">Better protection against future threats</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <svg className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                            <p className="font-medium text-gray-900">No Data Loss</p>
                            <p className="text-sm text-gray-600">All your passwords will be automatically re-encrypted</p>
                        </div>
                    </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
                    <div className="flex">
                        <svg className="h-5 w-5 text-yellow-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm text-yellow-800">
                            <strong>Important:</strong> Following the upgrade, please refrain from logging in. We will notify you once the system is ready.
                        </p>
                    </div>
                </div>

                <div className="flex space-x-3">
                    <button
                        type="button"
                        onClick={handleMandatoryMigration}
                        disabled={migrating}
                        className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-md hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all duration-200 flex items-center justify-center"
                    >
                        {migrating ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Upgrading Security...
                            </>
                        ) : (
                            <>
                                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                                Upgrade Now (Required)
                            </>
                        )}
                    </button>
                </div>

                <p className="text-xs text-gray-500 text-center mt-4">
                    This upgrade is mandatory for all users to ensure maximum security
                </p>
            </div>
        </div>
    );
};

export default RSAMigrationModalTrigger;
